const STORAGE_KEY = 'upwaste:organizationId';

// In-memory fallback when both localStorage and sessionStorage are unavailable
let memoryStorage: string | null = null;

/**
 * Storage availability detection
 * Tests actual read/write capability, not just existence
 */
const isStorageAvailable = (storage: Storage): boolean => {
  const testKey = '__storage_test__';
  try {
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

// Cache storage availability on module load for performance
let localStorageAvailable: boolean | null = null;
let sessionStorageAvailable: boolean | null = null;

const checkLocalStorage = (): boolean => {
  if (localStorageAvailable === null) {
    localStorageAvailable = typeof localStorage !== 'undefined' && isStorageAvailable(localStorage);
  }
  return localStorageAvailable;
};

const checkSessionStorage = (): boolean => {
  if (sessionStorageAvailable === null) {
    sessionStorageAvailable =
      typeof sessionStorage !== 'undefined' && isStorageAvailable(sessionStorage);
  }
  return sessionStorageAvailable;
};

/**
 * Get organization ID with fallback chain:
 * localStorage → sessionStorage → in-memory
 */
export const getStoredOrganizationId = (): string | null => {
  // Try localStorage first (persists across sessions)
  if (checkLocalStorage()) {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value) return value;
  }

  // Fall back to sessionStorage (persists within tab session)
  if (checkSessionStorage()) {
    const value = sessionStorage.getItem(STORAGE_KEY);
    if (value) return value;
  }

  // Fall back to in-memory (persists within page lifecycle)
  return memoryStorage;
};

/**
 * Set organization ID with fallback chain:
 * Attempts all available storage mechanisms for redundancy
 */
export const setStoredOrganizationId = (orgId: string): void => {
  // Always update in-memory as fallback
  memoryStorage = orgId;

  // Try localStorage (preferred - persists across sessions)
  if (checkLocalStorage()) {
    try {
      localStorage.setItem(STORAGE_KEY, orgId);
    } catch {
      // Safari private mode may throw QuotaExceededError
    }
  }

  // Also try sessionStorage for redundancy in case localStorage fails mid-session
  if (checkSessionStorage()) {
    try {
      sessionStorage.setItem(STORAGE_KEY, orgId);
    } catch {
      // Silently fail
    }
  }
};

/**
 * Clear organization ID from all storage mechanisms
 */
export const clearStoredOrganizationId = (): void => {
  memoryStorage = null;

  if (checkLocalStorage()) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silently fail
    }
  }

  if (checkSessionStorage()) {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silently fail
    }
  }
};

/**
 * Helper to validate MongoDB ObjectId format (24 character hex string)
 */
export const isValidObjectId = (id: string | undefined | null): boolean => {
  return !!id && /^[0-9a-fA-F]{24}$/.test(id);
};
